import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import $ from "jquery";
import wp from "wp";
import { Button, Icon } from "@devowl-wp/react-folder-tree";
import { idx, isElementInViewport } from "@devowl-wp/react-utils";
import { filter } from "./filter.js";
import { firstCreatedToolbar } from "./mediaLibraryShared.js";
import { isAttachmentsGalleryEdit } from "./modal.js";
import { restoreMediaViewSelection } from "./restoreMediaViewSelection.js";
import { createFolderSelector } from "../components/media/folderSelector.js";
import { resolveTreeStore } from "../hooks/tree/bridge/rmlMediaBridge.js";
import { ID_NONE } from "../integrations/defaultFolder.js";
import { decorateAttachmentShortcutIcon, removeDuplicateShortcuts } from "../integrations/shortcut.js";
import { scheduleWpUploaderPatch } from "../upload/pluploadBindings.js";
import { __ } from "../utils/i18n.js";
import { getProFeature } from "../utils/proFeatures.js";
import { rmlOpts } from "../utils/rmlOpts.js";
/**
 * Modify the media-views.js components (Backbone) in a way
 * to make them compatible with the AIOT component.
 */ function applyMediaViews() {
    const wpMedia = wp.media;
    if (!wpMedia || !idx(wpMedia, (m)=>m.view.Attachment.Library)) {
        return false;
    }
    // Create filter
    const RMLFilter = wpMedia.view.AttachmentFilters.RML = wpMedia.view.AttachmentFilters.extend(filter);
    // Hold selectedId to select previously selected id
    if (process.env.PLUGIN_CTX === "pro") {
    /* Silence is golden... Until you have PRO Version! */ }
    // Allow rml orderby
    wpMedia.model.Query.orderby.allowed.push("rml");
    const createComporator = (attachments, collection, fallbackComparator)=>function(...args) {
            const treeStore = resolveTreeStore(attachments.controller);
            const { selectedId } = treeStore;
            if (selectedId === "all") {
                return fallbackComparator.apply(this, args);
            }
            const [a, b] = args;
            let aO;
            let bO;
            if (collection.props.get("orderby") === "rml" && (aO = a.attributes.rmlGalleryOrder) && (bO = b.attributes.rmlGalleryOrder) && aO !== -1 && bO !== -1) {
                if (aO < bO) {
                    return -1;
                } else if (aO > bO) {
                    return 1;
                }
                return 0;
            } else if (typeof fallbackComparator === "function") {
                return fallbackComparator.apply(this, args);
            }
        };
    // Create rml orderby comparator
    const { Attachments } = wpMedia.view;
    wpMedia.view.Attachments = wpMedia.view.Attachments.extend({
        initialize (...args) {
            Attachments.prototype.initialize.apply(this, args);
            // Backbone view reference for collection.more override (must not use arrow functions there).
            // eslint-disable-next-line @typescript-eslint/no-this-alias -- WP media Attachments view captured for collection.more
            const attachmentsView = this;
            const { collection } = this;
            const { comparator } = collection;
            // Disable comporator in gallery-edit mode (dialog)
            if (isAttachmentsGalleryEdit(this)) {
                collection.comparator = undefined;
                this.initSortable();
                return;
            }
            /* Silence is golden... Until you have PRO Version! */ // Initially load a folder from the AppTree initialSelectedId state
            const oldMore = collection.more;
            attachmentsView._rmlInitialSetted = false;
            collection.more = function(...moreArgs) {
                if (!attachmentsView.views.parent || attachmentsView.controller.acf && [
                    attachmentsView.controller.acf.mode,
                    attachmentsView.controller.acf.data?.mode
                ].indexOf("edit") > -1) {
                    return oldMore.apply(this, moreArgs);
                }
                const bridge = attachmentsView.controller.rmlMediaBridge;
                const { toolbar } = attachmentsView.views.parent;
                const { model } = toolbar.get("rml_folder");
                const initialSelectedId = bridge?.initialSelectedId;
                if (bridge && initialSelectedId) {
                    if (!attachmentsView._rmlInitialSetted && initialSelectedId !== ID_NONE) {
                        model.set({
                            rml_folder: initialSelectedId === "all" ? "" : initialSelectedId
                        }, {
                            silent: false
                        });
                        attachmentsView._rmlInitialSetted = true;
                    }
                }
                if (model.get("rml_folder") !== undefined) {
                    return oldMore.apply(this, moreArgs);
                }
                return $.Deferred().resolveWith(attachmentsView).promise();
            };
        },
        /**
         * Override the default behavior when scrolling is relative to the document
         * height: upload.php. Instead of calculating with the scroll bottom use
         * the view-port approach.
         *
         * @internal
         */ scroll (...args) {
            let el = this.options.scrollElement;
            const overrideDefault = el === document && !isAttachmentsGalleryEdit(this);
            // The scroll event occurs on the document, but the element
            // that should be checked is the last grid item
            if (overrideDefault && !this.$el.hasClass("rml-loading")) {
                el = this.$el.children(":last");
                if (!$(el).is(":visible") || !this.collection.hasMore()) {
                    return;
                }
                const scrollTarget = $(el).get(0);
                if (scrollTarget && isElementInViewport(scrollTarget, true)) {
                    this.$el.addClass("rml-loading");
                    this.collection.more().done(()=>{
                        this.$el.removeClass("rml-loading");
                        this.scroll();
                    });
                }
            } else {
                Attachments.prototype.scroll.apply(this, args);
            }
        }
    });
    const oldRender = wpMedia.view.Attachment.Library.prototype.render;
    wpMedia.view.Attachment.Library.prototype.render = function(...renderArgs) {
        oldRender.apply(this, renderArgs);
        if (isAttachmentsGalleryEdit(this.views.parent)) {
            return;
        }
        decorateAttachmentShortcutIcon(this.$el, this.model);
    };
    // Modify attachments browser
    let timeoutReloadCount;
    const { AttachmentsBrowser } = wpMedia.view;
    wpMedia.view.AttachmentsBrowser = wpMedia.view.AttachmentsBrowser.extend({
        initialize (...initArgs) {
            AttachmentsBrowser.prototype.initialize.apply(this, initArgs);
            // Disable in gallery edit view
            if (isAttachmentsGalleryEdit(this)) {
                return;
            }
            // Events for attachments browsers collections
            let timeout;
            this.collection.on("change reset add remove", ()=>{
                clearTimeout(timeout);
                timeout = setTimeout(()=>{
                    this.controller.rmlMediaBridge?.bindAttachmentDrag();
                }, 50);
            });
            this.collection.on("remove", (...args)=>{
                const [model] = args;
                removeDuplicateShortcuts(model, this.collection);
            });
            // Listen to the ajax complete to refresh the folder counts
            $(document).ajaxComplete((e, xhs, req)=>{
                try {
                    if (req.data.indexOf("action=delete-post") > -1) {
                        clearTimeout(timeoutReloadCount);
                        const bridge = this.controller.rmlMediaBridge;
                        bridge && (timeoutReloadCount = setTimeout(()=>bridge.refreshFolderCounts(), 1800));
                    }
                } catch (e) {
                // Silence is golden.
                }
            });
        },
        createToolbar (...toolbarArgs) {
            AttachmentsBrowser.prototype.createToolbar.apply(this, toolbarArgs);
            // Disable in gallery edit view
            if (isAttachmentsGalleryEdit(this)) {
                return;
            }
            this.$el.data("backboneView", this);
            // Add new toolbar
            const obj = new RMLFilter({
                controller: this.controller,
                model: this.collection.props,
                priority: -81
            }).render();
            this.toolbar.set("rml_folder", obj);
            const toolbarTreeStore = resolveTreeStore(this.controller);
            const { modal } = this.controller.options;
            if (modal) {
                if (process.env.PLUGIN_CTX === "pro") {
                /* Silence is golden... Until you have PRO Version! */ } else {
                    // In lite version, create container for the simple FolderSelector
                    const folderSelectorContainer = new wpMedia.View({
                        className: "rml-attachment-filter-folder-selector"
                    });
                    const proTexts = getProFeature("insert-media-tree-view");
                    if (!proTexts) {
                        return;
                    }
                    this.toolbar.secondary.views.add(folderSelectorContainer, {
                        at: 1
                    });
                    createFolderSelector(folderSelectorContainer.el, undefined, {
                        title: __("Select folder"),
                        input: obj.$el.get(0),
                        nullable: true,
                        before: /*#__PURE__*/ _jsx("button", {
                            className: "button",
                            style: {
                                margin: "1px 10px 0 0"
                            },
                            onClick: ()=>this.collection.props.set({
                                    ignore: +new Date()
                                }),
                            children: /*#__PURE__*/ _jsx(Icon, {
                                type: "reload"
                            })
                        }),
                        onFetchTree: ({ slugs })=>{
                            obj.createFilters(slugs, toolbarTreeStore);
                            const useId = toolbarTreeStore.selected ? toolbarTreeStore.selectedId : "all";
                            rmlOpts.others.lastQueried = useId;
                            obj.$el.val(useId).change();
                        },
                        onSelect: (item)=>{
                            const useId = item === null ? "all" : item.id;
                            rmlOpts.others.lastQueried = useId;
                            obj.$el.val(useId).change();
                            // Check if folder needs refresh
                            if (toolbarTreeStore.foldersNeedsRefresh.indexOf(useId) > -1) {
                                toolbarTreeStore.removeFoldersNeedsRefresh(useId);
                                this.collection.props.set({
                                    ignore: +new Date()
                                });
                            }
                        },
                        children: /*#__PURE__*/ _jsxs("div", {
                            style: {
                                padding: "0px 20px 10px",
                                background: "#f9f9f9",
                                margin: "10px -5px -5px"
                            },
                            children: [
                                /*#__PURE__*/ _jsx("strong", {
                                    style: {
                                        padding: "10px 0px",
                                        display: "block",
                                        fontSize: 17
                                    },
                                    children: proTexts.title
                                }),
                                /*#__PURE__*/ _jsx("img", {
                                    src: `https://assets.devowl.io/in-app/wp-real-media-library/${proTexts.image}`,
                                    style: {
                                        width: "100%",
                                        height: "auto"
                                    }
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: proTexts.description
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    href: `${rmlOpts.others.proUrl}&feature=insert-media-tree-view`,
                                    target: "_blank",
                                    type: "primary",
                                    style: {
                                        textDecoration: "none",
                                        float: "right"
                                    },
                                    children: __("I want to learn more!")
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "clear"
                                })
                            ]
                        })
                    }, toolbarTreeStore);
                }
            } else {
                firstCreatedToolbar.resolve(this);
                scheduleWpUploaderPatch();
            }
        },
        /**
         * Create the view for "Select folder".
         */ createAttachments (...createArgs) {
            AttachmentsBrowser.prototype.createAttachments.apply(this, createArgs);
            this.attachmentsSelectFolder = new wpMedia.View({
                controller: this.controller,
                tagName: "p"
            });
            const { $el } = this.attachmentsSelectFolder;
            if (+rmlOpts.defaultFolder === ID_NONE) {
                $el.addClass("hidden no-media").html(__("Please select a folder to show items."));
                this.views.add(this.attachmentsSelectFolder);
            }
            if (!this.controller.isModeActive("grid")) {
                $el.css({
                    color: "#666",
                    "font-size": "18px",
                    padding: "30px 0 0 20px"
                });
            }
        },
        /**
         * Update handler to show created label for "Select folder" or "No media found".
         */ updateContent (...updateArgs) {
            AttachmentsBrowser.prototype.updateContent.apply(this, updateArgs);
            const noItemsView = this.controller.isModeActive("grid") ? this.attachmentsNoResults : this.uploader;
            this.dfd && this.dfd.done(()=>{
                const { attributes } = this.collection.props;
                if (attributes && attributes.rml_folder === undefined) {
                    noItemsView.$el.addClass("hidden");
                    this.attachmentsSelectFolder.$el.removeClass("hidden");
                } else {
                    this.attachmentsSelectFolder.$el.addClass("hidden");
                }
            });
        },
        remove (...removeArgs) {
            AttachmentsBrowser.prototype.remove.apply(this, removeArgs);
        }
    });
    return true;
}
/**
 * Enhanced Media Library compatibility and layout adjustment.
 */ function applyEmlGridLayout() {
    if ($("body").hasClass("eml-grid")) {
        const mediaGrid = $("#wp-media-grid");
        const offsetTop = mediaGrid.offset().top;
        const fnResize = ()=>{
            mediaGrid.css("height", $(window).height() - $("#wpadminbar").height() - 10);
        };
        const fnScroll = ()=>{
            const scrollTop = $(window).scrollTop();
            mediaGrid[0].style.top = `${scrollTop > offsetTop ? scrollTop : 0}px`;
        //mediaGrid.css("top", scrollTop > offsetTop ? scrollTop - offsetTop : 0);
        };
        // Centerize container
        $(window).on("resize", fnResize);
        fnResize();
        // Scroll container
        $(window).on("scroll", fnScroll);
        fnScroll();
    }
}
export { applyEmlGridLayout, applyMediaViews, restoreMediaViewSelection };
